import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiCoreHttp from '../../utils/api/apiCoreHttp';
import http from '../../utils/api/http';
import insuranceApi from '../../utils/api/insuranceApi';
import sftpApi from '../../utils/api/sftpApi';
import { toast } from 'react-toastify';

export const getClients = createAsyncThunk(
    'clients/getClients',
    async ({ name, page = 1, limit = 9 }) => {
        const { data } = await http.get(
            `api/v1/clients?name=${name}&page=${page}&limit=${limit}`,
        );
        return data;
    },
);

export const createClient = createAsyncThunk(
    'clients/createClient',
    async ({ body }, { rejectWithValue }) => {
        try {
            const { data } = await http.post('api/v1/client', body, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateClient = createAsyncThunk(
    'clients/editClient',
    async ({ id, body }, { rejectWithValue }) => {
        try {
            const { data } = await http.post(`api/v1/client/${id}`, body, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteClient = createAsyncThunk(
    'clients/deleteClient',
    async (id) => {
        await http.delete(`api/v1/client/${id}`);
    },
);

export const getClient = createAsyncThunk('clients/getClient', async (id) => {
    const { data } = await http.get(`api/v1/client/${id}`);
    return data.data;
});

export const getClientProducts = createAsyncThunk(
    'clients/getClientProducts',
    async ({ client, url }) => {
        const { data } = await apiCoreHttp.get(
            `${url}/api/v1/products/available`,
            {
                headers: {
                    Client: client,
                },
            },
        );
        return data;
    },
);

export const statusProductForClient = createAsyncThunk(
    'clients/storeClientProducts',
    async ({ action, body, client, url }) => {
        await apiCoreHttp.post(`${url}/api/v1/products/${action}`, body, {
            headers: {
                Client: client,
            },
        });
    },
);

export const statusAccountTypeForProduct = createAsyncThunk(
    'clients/storeClientProducts',
    async ({ action, body, client, url }) => {
        await apiCoreHttp.post(
            `${url}/api/v1/products/account-type/${action}`,
            body,
            {
                headers: {
                    Client: client,
                },
            },
        );
    },
);

export const storeClientPolicies = createAsyncThunk(
    'clients/storeClientPolicies',
    async ({ id, body }) => {
        const { data } = await http.patch(`api/v1/client/${id}/policy`, body);
        return data.data;
    },
);

export const getClientTermsAndPrivacy = createAsyncThunk(
    'clients/getClientTermsAndPrivacy',
    async (id) => {
        const { data } = await http.get(`api/v1/client/${id}/policy`);
        return data.data;
    },
);

export const deleteClientLogoByCountryCode = createAsyncThunk(
    'clients/deleteClientLogoByCountryCode',
    async ({ id, countryCode }) => {
        await http.patch(`api/v1/client/${id}/logo/${countryCode}/delete`);
    },
);

export const deleteClientLogoThumbByCountryCode = createAsyncThunk(
    'clients/deleteClientLogoThumbByCountryCode',
    async ({ id, countryCode }) => {
        await http.patch(
            `api/v1/client/${id}/logo-thumb/${countryCode}/delete`,
        );
    },
);

export const deleteClientFaviconByCountryCode = createAsyncThunk(
    'clients/deleteClientFaviconByCountryCode',
    async ({ id, countryCode }) => {
        await http.patch(`api/v1/client/${id}/favicon/${countryCode}/delete`);
    },
);

export const deleteClientEmailLogoByCountryCode = createAsyncThunk(
    'clients/deleteClientEmailLogoByCountryCode',
    async ({ id, countryCode }) => {
        await http.patch(
            `api/v1/client/${id}/logo-email/${countryCode}/delete`,
        );
    },
);

export const updateClientDrivers = createAsyncThunk(
    'clients/updateClientDrivers',
    async ({ id, body }) => {
        await http.patch(`api/v1/client/${id}/client-drivers`, body);
    },
);

export const updateClientSlackChannels = createAsyncThunk(
    'clients/updateClientSlackChannels',
    async ({ id, body }) => {
        await http.patch(`api/v1/client/${id}/slack-drivers`, body);
    },
);

export const getClientCoachMe = createAsyncThunk(
    'clients/getClientCoachMe',
    async (id) => {
        const { data } = await http.get(`api/v1/client/${id}/coach-me`);
        return data.data;
    },
);

export const importClientCoachMe = createAsyncThunk(
    'clients/importClientCoachMe',
    async ({ id, body }) => {
        await http.post(`api/v1/client/${id}/coach-me/import`, body);
    },
);

export const getCrmUsersV1 = createAsyncThunk(
    'clients/getCrmUsers',
    async ({ url, client }) => {
        const { data } = await apiCoreHttp.get(`${url}/api/v1/crm/users`, {
            headers: {
                Client: client,
            },
        });
        return data.data;
    },
);

export const getCrmUsersV2 = createAsyncThunk(
    'clients/getCrmUsers',
    async ({ url, client }) => {
        const { data } = await apiCoreHttp.get(`${url}/api/v2/crm/users`, {
            headers: {
                Client: client,
            },
        });
        return data.data;
    },
);

export const createCrmUserV1 = createAsyncThunk(
    'clients/createCrmUser',
    async ({ body, url, client }, { rejectWithValue }) => {
        try {
            const { data } = await apiCoreHttp.post(
                `${url}/api/v1/crm/user`,
                body,
                {
                    headers: {
                        Client: client,
                    },
                },
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const createCrmUserV2 = createAsyncThunk(
    'clients/createCrmUser',
    async ({ body, url, client }, { rejectWithValue }) => {
        try {
            const { data } = await apiCoreHttp.post(
                `${url}/api/v2/crm/users`,
                body,
                {
                    headers: {
                        Client: client,
                    },
                },
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateCrmUserV1 = createAsyncThunk(
    'clients/updateCrmUser',
    async ({ id, body, url, client }, { rejectWithValue }) => {
        try {
            const { data } = await apiCoreHttp.put(
                `${url}/api/v1/crm/user/${id}`,
                body,
                {
                    headers: {
                        Client: client,
                    },
                },
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateCrmUserV2 = createAsyncThunk(
    'clients/updateCrmUser',
    async ({ id, body, url, client }, { rejectWithValue }) => {
        try {
            const { data } = await apiCoreHttp.put(
                `${url}/api/v2/crm/users/${id}`,
                body,
                {
                    headers: {
                        Client: client,
                    },
                },
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteCrmUserV1 = createAsyncThunk(
    'clients/deleteCrmUser',
    async ({ id, url, client }, { rejectWithValue }) => {
        try {
            await apiCoreHttp.delete(`${url}/api/v1/crm/user/${id}`, {
                headers: {
                    Client: client,
                },
            });
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteCrmUserV2 = createAsyncThunk(
    'clients/deleteCrmUser',
    async ({ id, url, client }, { rejectWithValue }) => {
        try {
            await apiCoreHttp.delete(`${url}/api/v2/crm/users/${id}`, {
                headers: {
                    Client: client,
                },
            });
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const verifyCrmUser = createAsyncThunk(
    'clients/verifyCrmUser',
    async ({ id, url, client }, { rejectWithValue }) => {
        try {
            await apiCoreHttp.post(
                `${url}/api/v2/crm/users/${id}/password/forgot`,
                {
                    headers: {
                        Client: client,
                    },
                },
            );
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getClientPackages = createAsyncThunk(
    'clients/getClientPackages',
    async (clientId) => {
        const { data } = await insuranceApi.get(
            `api/v1/packages/client/${clientId}`,
        );
        return data.data;
    },
);

export const updateClientPackages = createAsyncThunk(
    'clients/updateClientPackages',
    async ({ clientId, body }, { rejectWithValue }) => {
        try {
            const { data } = await insuranceApi.put(
                `api/v1/packages/client/${clientId}`,
                body,
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getClientSftp = createAsyncThunk(
    'clients/getClientSftp',
    async ({ id }) => {
        const { data } = await sftpApi.get(`api/v1/client/${id}/sftp`);

        return data.data;
    },
);

export const updateClientSftp = createAsyncThunk(
    'clients/updateClientSftp',
    async ({ id, body }, { rejectWithValue }) => {
        try {
            const { data } = await sftpApi.put(
                `api/v1/client/${id}/sftp`,
                body,
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getClientDataStore = createAsyncThunk(
    'clients/getClientDataStore',
    async ({ id, collectionName }, { rejectWithValue }) => {
        try {
            const { data } = await http.get(
                `api/v1/collections/${collectionName}/datastore/${id}`,
            );

            return data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

// create or update document
export const createClientDataStore = createAsyncThunk(
    'clients/createClientDataStore',
    async ({ id, body, collectionName }, { rejectWithValue }) => {
        try {
            const { data } = await http.post(
                `api/v1/collections/${collectionName}/datastore/${id}`,
                body,
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteClientFieldFromDataStore = createAsyncThunk(
    'clients/deleteClientFieldFromDataStore',
    async ({ id, collectionName, fieldName }, { rejectWithValue }) => {
        try {
            const { data } = await http.delete(
                `api/v1/collections/${collectionName}/datastore/${id}/${fieldName}`,
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getClientWelcomeReminders = createAsyncThunk(
    'clients/getClientWelcomeReminders',
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await http.get(`api/v1/client/${id}/wct`);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const storeClientWelcomeReminders = createAsyncThunk(
    'clients/storeClientWelcomeReminders',
    async ({ id, body }, { rejectWithValue }) => {
        try {
            const { data } = await http.patch(`api/v1/client/${id}/wct`, body);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteClientWelcomeReminder = createAsyncThunk(
    'clients/deleteClientWelcomeReminder',
    async ({ id, timingId }, { rejectWithValue }) => {
        try {
            const { data } = await http.delete(
                `api/v1/client/${id}/wct/${timingId}`,
            );
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getClientsMaintenanceProgress = createAsyncThunk(
    'clients/getClientsMaintenanceProgress',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await apiCoreHttp.get(
                `api/v1/clients/maintenance`,
            );
            return data;
        } catch (error) {
            if (error.response.status === 404) {
                toast.warning(
                    'Sorry, Maintenance list data not available. Try again later.',
                );
            }
            return rejectWithValue(error.response.data);
        }
    },
);

export const getClientHealthCheck = createAsyncThunk(
    'clients/getClientsHealthCheck',
    async (url, { rejectWithValue }) => {
        try {
            const { status } = await apiCoreHttp.get(
                `${url}/api/v1/health-check`,
            );
            return status;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getClientReleaseVersion = createAsyncThunk(
    'clients/getClientReleaseVersion',
    async (url, { rejectWithValue }) => {
        try {
            const { data } = await apiCoreHttp.get(`${url}/api/v1/version`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getRoles = createAsyncThunk(
    'clients/getRoles',
    async ({ url, client }) => {
        const { data } = await apiCoreHttp.get(`${url}/api/v2/crm/roles`, {
            headers: {
                Client: client,
            },
        });
        return data.data;
    },
);

export const loginToCrm = createAsyncThunk(
    'clients/loginToCrm',
    async ({ id, body }, { rejectWithValue }) => {
        try {
            const { data } = await http.post(
                `api/v1/client/${id}/sso/temp-token`,
                body,
            );
            return data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    },
);

const initialState = {
    clientsLoadingStatus: 'idle',
    clientLoadingStatus: 'idle',
    clientProductsLoadingStatus: 'idle',
    clientProductsUpdateStatus: 'idle',
    clientTermsAndPrivacyStatus: 'idle',
    clientCoachMeStatus: 'idle',
    clientCrmUsersStatus: 'idle',
    clientPackagesLoadingStatus: 'idle',
    clientSftpLoadingStatus: 'idle',
    clientNotificationLoadingStatus: 'idle',
    clientGeneralLoadingStatus: 'idle',
    clientWelcomeRemindersLoadingStatus: 'idle',
    clientMaintenanceProgressLoadingStatus: 'idle',
    rolesLoadingStatus: 'idle',
    clients: [],
    client: {},
    clientProducts: [],
    clientPackages: [],
    clientSftp: {},
    clientDataStore: {},
    clientWelcomeReminders: {},
    clientsMaintenanceProgress: [],
    coachMe: [],
    crmUsers: [],
    links: {},
    meta: {},
    termsAndPrivacy: {},
    errors: {},
    roles: [],
};

export const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {},
    extraReducers: {
        [getClients.pending]: (state, action) => {
            state.clientsLoadingStatus = 'loading';
        },
        [getClients.fulfilled]: (state, action) => {
            state.clientsLoadingStatus = 'succeeded';
            state.clients = action.payload.data;
            state.links = action.payload.links;
            state.meta = action.payload.meta;
        },
        [getClients.rejected]: (state, action) => {
            state.clientLoadingStatus = 'failed';
            state.clients = [];
            state.errors = action.error;
        },
        [getClient.pending]: (state, action) => {
            state.clientLoadingStatus = 'loading';
        },
        [getClient.fulfilled]: (state, action) => {
            state.clientLoadingStatus = 'succeeded';
            state.client = action.payload;
        },
        [getClient.rejected]: (state, action) => {
            state.clientLoadingStatus = 'failed';
            state.errors = action.error;
        },
        [getClientProducts.pending]: (state, action) => {
            state.clientProductsLoadingStatus = 'loading';
        },
        [getClientProducts.fulfilled]: (state, action) => {
            state.clientProductsLoadingStatus = 'succeeded';
            state.clientProducts = action.payload;
        },
        [getClientProducts.rejected]: (state, action) => {
            state.clientProductsLoadingStatus = 'failed';
            state.clientProducts = [];
            state.errors = action.error;
        },
        [statusProductForClient.pending]: (state, action) => {
            state.clientProductsUpdateStatus = 'loading';
        },
        [statusProductForClient.fulfilled]: (state, action) => {
            state.clientProductsUpdateStatus = 'succeeded';
        },
        [statusProductForClient.rejected]: (state, action) => {
            state.clientProductsUpdateStatus = 'failed';
            state.errors = action.error;
        },
        [getClientTermsAndPrivacy.pending]: (state, action) => {
            state.clientTermsAndPrivacyStatus = 'loading';
        },
        [getClientTermsAndPrivacy.fulfilled]: (state, action) => {
            state.clientTermsAndPrivacyStatus = 'succeeded';
            state.termsAndPrivacy = action.payload;
        },
        [getClientTermsAndPrivacy.rejected]: (state, action) => {
            state.clientTermsAndPrivacyStatus = 'failed';
            state.errors = action.error;
        },
        [getClientCoachMe.pending]: (state, action) => {
            state.clientCoachMeStatus = 'loading';
        },
        [getClientCoachMe.fulfilled]: (state, action) => {
            state.clientCoachMeStatus = 'succeeded';
            state.coachMe = action.payload;
        },
        [getClientCoachMe.rejected]: (state, action) => {
            state.clientCoachMeStatus = 'failed';
            state.coachMe = [];
            state.errors = action.error;
        },
        [getCrmUsersV1.pending]: (state, action) => {
            state.clientCrmUsersStatus = 'loading';
        },
        [getCrmUsersV1.fulfilled]: (state, action) => {
            state.clientCrmUsersStatus = 'succeeded';
            state.crmUsers = action.payload;
        },
        [getCrmUsersV1.rejected]: (state, action) => {
            state.clientCrmUsersStatus = 'failed';
            state.crmUsers = [];
            state.errors = action.error;
        },
        [getCrmUsersV2.pending]: (state, action) => {
            state.clientCrmUsersStatus = 'loading';
        },
        [getCrmUsersV2.fulfilled]: (state, action) => {
            state.clientCrmUsersStatus = 'succeeded';
            state.crmUsers = action.payload;
        },
        [getCrmUsersV2.rejected]: (state, action) => {
            state.clientCrmUsersStatus = 'failed';
            state.crmUsers = [];
            state.errors = action.error;
        },
        [getClientPackages.pending]: (state, action) => {
            state.clientPackagesLoadingStatus = 'loading';
        },
        [getClientPackages.fulfilled]: (state, action) => {
            state.clientPackagesLoadingStatus = 'succeeded';
            state.clientPackages = action.payload;
        },
        [getClientPackages.rejected]: (state, action) => {
            state.clientPackagesLoadingStatus = 'failed';
            state.errors = action.error;
        },
        [getClientSftp.pending]: (state, action) => {
            state.clientSftpLoadingStatus = 'loading';
        },
        [getClientSftp.fulfilled]: (state, action) => {
            state.clientSftpLoadingStatus = 'succeeded';
            state.clientSftp = action.payload;
        },
        [getClientSftp.rejected]: (state, action) => {
            state.clientSftpLoadingStatus = 'failed';
            state.errors = action.error;
        },
        [getClientDataStore.pending]: (state, action) => {
            state.clientDataStoreLoadingStatus = 'loading';
        },
        [getClientDataStore.rejected]: (state, action) => {
            state.clientDataStore = action.payload;
            state.clientDataStoreLoadingStatus = 'failed';
            state.errors = action.error;
        },
        [getClientDataStore.fulfilled]: (state, action) => {
            state.clientDataStoreLoadingStatus = 'succeeded';
            state.clientDataStore = action.payload;
        },
        [getClientWelcomeReminders.pending]: (state, action) => {
            state.clientWelcomeRemindersLoadingStatus = 'loading';
        },
        [getClientWelcomeReminders.rejected]: (state, action) => {
            state.clientWelcomeReminders = action.payload;
            state.clientWelcomeRemindersLoadingStatus = 'failed';
        },
        [getClientWelcomeReminders.fulfilled]: (state, action) => {
            state.clientWelcomeReminders = action.payload;
            state.clientWelcomeRemindersLoadingStatus = 'succeeded';
        },
        [getClientsMaintenanceProgress.pending]: (state, action) => {
            state.clientsMaintenanceProgressLoadingStatus = 'loading';
        },
        [getClientsMaintenanceProgress.rejected]: (state) => {
            state.clientsMaintenanceProgressLoadingStatus = 'failed';
        },
        [getClientsMaintenanceProgress.fulfilled]: (state, action) => {
            state.clientsMaintenanceProgress = action.payload;
            state.clientsMaintenanceProgressLoadingStatus = 'succeeded';
        },
        [getClientReleaseVersion.pending]: (state, action) => {
            state.clientReleaseVersionLoadingStatus = 'loading';
        },
        [getClientReleaseVersion.rejected]: (state, action) => {
            state.clientReleaseVersion = action.payload;
            state.clientReleaseVersionLoadingStatus = 'failed';
            state.errors = action.error;
        },
        [getClientReleaseVersion.fulfilled]: (state, action) => {
            state.clientReleaseVersionLoadingStatus = 'succeeded';
            state.clientReleaseVersion = action.payload;
        },
        [getRoles.pending]: (state, action) => {
            state.rolesLoadingStatus = 'loading';
        },
        [getRoles.fulfilled]: (state, action) => {
            state.rolesLoadingStatus = 'succeeded';
            state.roles = action.payload;
        },
        [getRoles.rejected]: (state, action) => {
            state.rolesLoadingStatus = 'failed';
            state.roles = [];
            state.errors = action.error;
        },
    },
});

/* Selectors */
export const allClients = (state) => state.clients.clients;
export const clientsLoadingStatus = (state) =>
    state.clients.clientsLoadingStatus;
export const clientProductsLoadingStatus = (state) =>
    state.clients.clientProductsLoadingStatus;
export const clientsMeta = (state) => state.clients.meta;
export const oneClient = (state) => state.clients.client;
export const clientProducts = (state) => state.clients.clientProducts;
export const clientTermsAndPrivacy = (state) => state.clients.termsAndPrivacy;
export const clientTermsAndPrivacyStatus = (state) =>
    state.clients.clientTermsAndPrivacyStatus;
export const clientCoachMe = (state) => state.clients.coachMe;
export const clientCoachMeStatus = (state) => state.clients.clientCoachMeStatus;
export const clientCrmUsers = (state) => state.clients.crmUsers;
export const clientCrmUsersStatus = (state) =>
    state.clients.clientCrmUsersStatus;
export const clientPackages = (state) => state.clients.clientPackages;
export const clientPackagesLoadingStatus = (state) =>
    state.clients.clientPackagesLoadingStatus;
export const clientSftp = (state) => state.clients.clientSftp;
export const clientSftpLoadingStatus = (state) =>
    state.clients.clientSftpLoadingStatus;
export const clientDataStore = (state) => state.clients.clientDataStore;
export const clientDataStoreLoadingStatus = (state) =>
    state.clients.clientDataStoreLoadingStatus;
export const clientWelcomeRemindersLoadingStatus = (state) =>
    state.clients.clientWelcomeRemindersLoadingStatus;
export const clientWelcomeReminders = (state) =>
    state.clients.clientWelcomeReminders;
export const clientsMaintenanceProgressLoadingStatus = (state) =>
    state.clients.clientsMaintenanceProgressLoadingStatus;
export const clientsMaintenanceProgress = (state) =>
    state.clients.clientsMaintenanceProgress;
export const clientReleaseVersion = (state) =>
    state.clients.clientReleaseVersion;
export const clientReleaseVersionLoadingStatus = (state) =>
    state.clients.clientReleaseVersionLoadingStatus;
export const allRoles = (state) => state.clients.roles;
export default clientsSlice.reducer;
